import React, { useState, useEffect } from 'react';
import { Database, Download, Upload, Shield, AlertCircle, CheckCircle, Clock, HardDrive, Calendar, Activity, HelpCircle, Info, FolderOpen, Cloud, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

import apiClient from '../../utils/apiClient';

interface BackupInfo {
  fileName: string;
  type: string;
  size: string;
  createdAt: string;
  status: 'success' | 'pending' | 'error';
}

interface BackupStatus {
  lastBackup: BackupInfo | null;
  totalBackups: number;
  totalSize: string;
  diskUsage: number;
  nextScheduledBackup: string;
  isAutoBackupEnabled: boolean;
}

const BackupSettings: React.FC = () => {
  const [status, setStatus] = useState<BackupStatus | null>(null);
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showGenerationHelp, setShowGenerationHelp] = useState(false);
  const [showLocalBackupHelp, setShowLocalBackupHelp] = useState(false);

  useEffect(() => {
    fetchBackupStatus();
    fetchBackupList();
  }, []);

  const fetchBackupStatus = async () => {
    try {
      const response = await apiClient.get('/backup/status');
      setStatus(response.data);
    } catch (error) {
      console.error('バックアップ状況の取得に失敗しました:', error);
    }
  };

  const fetchBackupList = async () => {
    try {
      const response = await apiClient.get('/backup/list');
      setBackups(response.data);
    } catch (error) {
      console.error('バックアップリストの取得に失敗しました:', error);
    }
  };

  const executeBackup = async () => {
    setExecuting(true);
    setMessage({ type: 'info', text: 'バックアップを実行中...' });

    try {
      const response = await apiClient.post('/backup/execute');
      if (response.data.success) {
        setMessage({ type: 'success', text: 'バックアップが正常に完了しました' });
        fetchBackupStatus();
        fetchBackupList();
      } else {
        setMessage({ type: 'error', text: 'バックアップに失敗しました' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'バックアップの実行中にエラーが発生しました' });
    } finally {
      setExecuting(false);
    }
  };

  const verifyBackup = async (fileName: string) => {
    setLoading(true);
    try {
      const response = await apiClient.post('/backup/verify', { fileName });
      if (response.data.valid) {
        setMessage({ type: 'success', text: 'バックアップの整合性が確認されました' });
      } else {
        setMessage({ type: 'error', text: 'バックアップファイルに問題があります' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '検証中にエラーが発生しました' });
    } finally {
      setLoading(false);
    }
  };

  const downloadBackup = async (fileName: string) => {
    try {
      const response = await apiClient.get(`/backup/download/${fileName}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setMessage({ type: 'success', text: 'ダウンロードが開始されました' });
    } catch (error) {
      setMessage({ type: 'error', text: 'ダウンロードに失敗しました' });
    }
  };

  const getBackupTypeColor = (type: string) => {
    switch (type) {
      case 'daily': return 'bg-blue-100 text-blue-800';
      case 'weekly': return 'bg-green-100 text-green-800';
      case 'monthly': return 'bg-yellow-100 text-yellow-800';
      case 'yearly': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDiskUsageColor = (usage: number) => {
    if (usage > 80) return 'bg-red-500';
    if (usage > 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <Database className="text-blue-500" />
          バックアップ管理
        </h1>
        <p className="text-gray-600 mt-2">システムデータのバックアップ管理と復元</p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' ? 'bg-green-50 text-green-800' :
          message.type === 'error' ? 'bg-red-50 text-red-800' :
          'bg-blue-50 text-blue-800'
        }`}>
          {message.type === 'success' ? <CheckCircle /> :
           message.type === 'error' ? <AlertCircle /> :
           <Clock className="animate-spin" />}
          {message.text}
        </div>
      )}

      {/* ステータスカード */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <Clock className="text-blue-500" size={24} />
            <span className="text-sm text-gray-500">最終バックアップ</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            {status?.lastBackup ?
              format(new Date(status.lastBackup.createdAt), 'HH:mm', { locale: ja }) :
              '--:--'}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            {status?.lastBackup ?
              format(new Date(status.lastBackup.createdAt), 'yyyy年MM月dd日', { locale: ja }) :
              'なし'}
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <Database className="text-green-500" size={24} />
            <span className="text-sm text-gray-500">総バックアップ数</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{status?.totalBackups || 0}</p>
          <p className="text-sm text-gray-600 mt-1">総容量: {status?.totalSize || '0MB'}</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <HardDrive className="text-yellow-500" size={24} />
            <span className="text-sm text-gray-500">ディスク使用率</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{status?.diskUsage || 0}%</p>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
            <div
              className={`h-2 rounded-full ${getDiskUsageColor(status?.diskUsage || 0)}`}
              style={{ width: `${status?.diskUsage || 0}%` }}
            />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <Calendar className="text-purple-500" size={24} />
            <span className="text-sm text-gray-500">次回予定</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">03:00</p>
          <p className="text-sm text-gray-600 mt-1">毎日自動実行</p>
        </div>
      </div>

      {/* 手動バックアップ */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">手動バックアップ</h2>
              <p className="text-sm text-gray-600 mt-1">今すぐバックアップを実行できます</p>
            </div>
            {/* かんたん解説スイッチ */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">かんたん解説</span>
              <button
                onClick={() => setShowTutorial(!showTutorial)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showTutorial ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showTutorial ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
        <div className="p-6">
          {/* チュートリアル表示 */}
          {showTutorial && (
            <>
              <div className="mb-6 space-y-4">
                {/* バックアップの基本説明 */}
                <div className="p-4 bg-blue-50 border-l-4 border-blue-400 rounded-lg">
                  <div className="flex">
                    <HelpCircle className="text-blue-400 flex-shrink-0" size={20} />
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <strong>🔍 バックアップとは？</strong><br />
                        お店の大切なデータ（売上、顧客情報、在庫など）を安全に保管する仕組みです。<br />
                        パソコンの故障や誤操作でデータが失われても、バックアップから復元できます。
                      </p>
                    </div>
                  </div>
                </div>

                {/* 自動バックアップの説明 */}
                <div className="p-4 bg-green-50 border-l-4 border-green-400 rounded-lg">
                  <div className="flex">
                    <RefreshCw className="text-green-400 flex-shrink-0" size={20} />
                    <div className="ml-3">
                      <p className="text-sm text-green-700">
                        <strong>🔄 自動バックアップ</strong><br />
                        毎日深夜3時に自動でバックアップが実行されます。<br />
                        通常は何もする必要はありませんが、大きな変更（商品の一括登録など）の前は手動でバックアップすることをお勧めします。
                      </p>
                    </div>
                  </div>
                </div>

                {/* 手動バックアップのタイミング */}
                <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
                  <div className="flex">
                    <Info className="text-yellow-400 flex-shrink-0" size={20} />
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>⏰ いつ手動バックアップすべき？</strong><br />
                        • 商品や価格を大量に変更する前<br />
                        • 重要な設定を変更する前<br />
                        • 月末の締め作業の前後<br />
                        • システムアップデートの前
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          <button
            onClick={executeBackup}
            disabled={executing}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {executing ? (
              <>
                <Clock className="animate-spin" size={20} />
                実行中...
              </>
            ) : (
              <>
                <Database size={20} />
                今すぐバックアップ
              </>
            )}
          </button>
        </div>
      </div>

      {/* バックアップ履歴 */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-800">バックアップ履歴</h2>
          <p className="text-sm text-gray-600 mt-1">過去のバックアップ一覧</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ファイル名
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  タイプ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  サイズ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  作成日時
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  アクション
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {backups.map((backup, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {backup.fileName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getBackupTypeColor(backup.type)}`}>
                      {backup.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {backup.size}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(new Date(backup.createdAt), 'yyyy/MM/dd HH:mm', { locale: ja })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => verifyBackup(backup.fileName)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                      disabled={loading}
                    >
                      <Shield size={18} />
                    </button>
                    <button
                      onClick={() => downloadBackup(backup.fileName)}
                      className="text-green-600 hover:text-green-900"
                    >
                      <Download size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 世代管理ポリシー */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-800">
            <Activity className="inline mr-2" size={20} />
            世代管理ポリシー
          </h3>
          <button
            onClick={() => setShowGenerationHelp(!showGenerationHelp)}
            className="text-blue-500 hover:text-blue-700 flex items-center gap-1 text-sm"
          >
            <HelpCircle size={16} />
            {showGenerationHelp ? '説明を閉じる' : '説明を見る'}
          </button>
        </div>

        {/* 世代管理の説明 */}
        {showGenerationHelp && (
          <div className="mb-4 p-4 bg-white rounded-lg border border-blue-200">
            <p className="text-sm text-gray-700 mb-2">
              <strong>📚 世代管理とは？</strong>
            </p>
            <p className="text-sm text-gray-600 mb-3">
              バックアップファイルを複数世代（過去のバージョン）保管する仕組みです。<br />
              例えば、今日のデータに問題があった場合、昨日や先週のバックアップから復元できます。
            </p>
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-xs text-gray-600 font-semibold mb-2">保管期間の例：</p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• 過去7日分：毎日のバックアップを保管（最近のデータを細かく復元可能）</li>
                <li>• 過去4週分：毎週日曜のバックアップを保管（1ヶ月前まで週単位で復元可能）</li>
                <li>• 過去12ヶ月分：毎月1日のバックアップを保管（1年前まで月単位で復元可能）</li>
                <li>• 過去5年分：毎年1月1日のバックアップを保管（長期保管用）</li>
              </ul>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <p className="font-medium text-gray-700">日次バックアップ</p>
            <p className="text-sm text-gray-600">最新7日分保持</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">週次バックアップ</p>
            <p className="text-sm text-gray-600">最新4週分保持（日曜日）</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">月次バックアップ</p>
            <p className="text-sm text-gray-600">最新12ヶ月分保持（1日）</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">年次バックアップ</p>
            <p className="text-sm text-gray-600">最新5年分保持（1月1日）</p>
          </div>
        </div>
      </div>

      {/* ローカルバックアップの説明 */}
      <div className="mt-8 bg-green-50 rounded-lg p-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-800">
            <FolderOpen className="inline mr-2" size={20} />
            ローカルバックアップ
          </h3>
          <button
            onClick={() => setShowLocalBackupHelp(!showLocalBackupHelp)}
            className="text-green-600 hover:text-green-700 flex items-center gap-1 text-sm"
          >
            <HelpCircle size={16} />
            {showLocalBackupHelp ? '説明を閉じる' : '説明を見る'}
          </button>
        </div>

        {/* ローカルバックアップの説明 */}
        {showLocalBackupHelp && (
          <div className="mb-4 p-4 bg-white rounded-lg border border-green-200">
            <p className="text-sm text-gray-700 mb-2">
              <strong>💾 ローカルバックアップとは？</strong>
            </p>
            <p className="text-sm text-gray-600 mb-3">
              お使いのパソコン内にバックアップファイルを保存する方法です。
            </p>

            <div className="space-y-3">
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-700 font-semibold mb-1">📁 保存場所：</p>
                <p className="text-xs text-gray-600 font-mono bg-white px-2 py-1 rounded">
                  C:\waton\waton-pos\backup\
                </p>
              </div>

              <div className="bg-blue-50 p-3 rounded">
                <p className="text-xs text-gray-700 font-semibold mb-1">🔄 手動バックアップの方法：</p>
                <ol className="text-xs text-gray-600 space-y-1">
                  <li>1. 上の「今すぐバックアップ」ボタンをクリック</li>
                  <li>2. バックアップが完了するまで待つ（約10秒）</li>
                  <li>3. 完了メッセージが表示されたら成功！</li>
                </ol>
              </div>

              <div className="bg-yellow-50 p-3 rounded">
                <p className="text-xs text-gray-700 font-semibold mb-1">⚠️ 注意点：</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• パソコンが故障した場合、ローカルバックアップも失われる可能性があります</li>
                  <li>• 定期的にUSBメモリなど外部メディアにコピーすることをお勧めします</li>
                  <li>• OneDriveなどクラウドストレージへの自動同期も推奨されます</li>
                </ul>
              </div>

              <div className="bg-green-50 p-3 rounded">
                <p className="text-xs text-gray-700 font-semibold mb-1">💡 USBへのコピー方法：</p>
                <ol className="text-xs text-gray-600 space-y-1">
                  <li>1. USBメモリをパソコンに挿入</li>
                  <li>2. エクスプローラーで <span className="font-mono">C:\waton\waton-pos\backup\</span> を開く</li>
                  <li>3. 最新のバックアップファイル（.db）を選択</li>
                  <li>4. 右クリック → コピー</li>
                  <li>5. USBメモリに貼り付け</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg">
            <p className="font-medium text-gray-700 flex items-center gap-2">
              <Database size={16} className="text-green-500" />
              バックアップファイル形式
            </p>
            <p className="text-sm text-gray-600 mt-1">SQLite データベース（.db）</p>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <p className="font-medium text-gray-700 flex items-center gap-2">
              <HardDrive size={16} className="text-blue-500" />
              平均ファイルサイズ
            </p>
            <p className="text-sm text-gray-600 mt-1">約10-50MB</p>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <p className="font-medium text-gray-700 flex items-center gap-2">
              <Cloud size={16} className="text-purple-500" />
              推奨バックアップ先
            </p>
            <p className="text-sm text-gray-600 mt-1">OneDrive / USBメモリ</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupSettings;